import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import {
  FaLock,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaCheckCircle,
  FaExclamationCircle,
  FaArrowLeft,
  FaKey,
} from "react-icons/fa";

import { logoutUser } from "../../redux/slices/authSlice";
import { changePassword } from "../../redux/apis/normalUser/changePasswordThunk";

// ==================================================
// PASSWORD FIELD COMPONENT
// IMPORTANT:
// Keep this OUTSIDE ChangePasswordPage.
// This prevents the input from being recreated
// on every keystroke and losing focus.
// ==================================================

const PasswordField = ({
  id,
  label,
  value,
  onChange,
  showPassword,
  setShowPassword,
  placeholder,
  setError,
  disabled = false,
}) => {
  return (
    <div className="min-w-0">
      <label
        htmlFor={id}
        className="
          mb-1.5
          block
          text-xs
          font-semibold
          text-slate-700
          sm:text-sm
        "
      >
        {label}
      </label>

      <div className="relative min-w-0">
        <FaLock
          className="
            pointer-events-none
            absolute
            left-3.5
            top-1/2
            z-10
            -translate-y-1/2
            text-sm
            text-slate-400
          "
        />

        <input
          id={id}
          name={id}
          type={showPassword ? "text" : "password"}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            setError("");
          }}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={
            id === "oldPassword" ? "current-password" : "new-password"
          }
          className="
            h-12
            w-full
            min-w-0
            rounded-xl
            border
            border-slate-200
            bg-white
            px-11
            pr-11
            text-[16px]
            text-slate-800
            outline-none
            transition
            placeholder:text-slate-400
            focus:border-sky-400
            focus:ring-4
            focus:ring-sky-100
            disabled:cursor-not-allowed
            disabled:bg-slate-100
            disabled:text-slate-400
            sm:text-sm
          "
        />

        <button
          type="button"
          onClick={() => setShowPassword((prev) => !prev)}
          disabled={disabled}
          className="
            absolute
            right-2
            top-1/2
            flex
            h-9
            w-9
            -translate-y-1/2
            items-center
            justify-center
            rounded-lg
            text-slate-400
            transition
            hover:bg-sky-50
            hover:text-sky-600
            disabled:cursor-not-allowed
            disabled:opacity-50
          "
          aria-label={showPassword ? `Hide ${label}` : `Show ${label}`}
        >
          {showPassword ? <FaEyeSlash /> : <FaEye />}
        </button>
      </div>
    </div>
  );
};

// ==================================================
// CHANGE PASSWORD PAGE
// ==================================================

const ChangePasswordPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ==================================================
  // REDUX STATE
  // ==================================================

  const {
    user,
    token,
    isAuthenticated,
    loading: authLoading,
  } = useSelector((state) => state.auth);

  // ==================================================
  // LOCAL STATE
  // ==================================================

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ==================================================
  // AUTH REDIRECT
  // ==================================================

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, authLoading, navigate]);

  // ==================================================
  // VALIDATION
  // ==================================================

  const validatePassword = () => {
    if (!oldPassword || !newPassword || !confirmPassword) {
      return "All fields are required.";
    }

    if (newPassword.length < 6) {
      return "New password must be at least 6 characters.";
    }

    if (newPassword !== confirmPassword) {
      return "New password and confirm password do not match.";
    }

    if (oldPassword === newPassword) {
      return "New password cannot be the same as the old password.";
    }

    return "";
  };

  // ==================================================
  // SUBMIT
  // ==================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setSuccessMessage("");

    const validationError = validatePassword();

    if (validationError) {
      setError(validationError);
      return;
    }

    if (!user?._id || !token) {
      setError("Your login session has expired. Please login again.");
      return;
    }

    try {
      setLoading(true);

      const resultAction = await dispatch(
        changePassword({
          userId: user._id,
          oldPassword,
          newPassword,
          token,
        }),
      );

      if (changePassword.fulfilled.match(resultAction)) {
        setSuccessMessage(
          resultAction.payload?.message || "Password changed successfully.",
        );

        setOldPassword("");
        setNewPassword("");
        setConfirmPassword("");

        // Logout after successful password change
        setTimeout(() => {
          dispatch(logoutUser());
          navigate("/login");
        }, 2000);
      } else {
        const errorMessage = resultAction.payload;

        const normalizedError =
          typeof errorMessage === "string"
            ? errorMessage
            : errorMessage?.message || "";

        // Unauthorized / expired session
        if (
          normalizedError.toLowerCase().includes("unauthorized") ||
          normalizedError.includes("401")
        ) {
          dispatch(logoutUser());
          navigate("/login");
          return;
        }

        setError(
          normalizedError || "Unable to change password. Please try again.",
        );
      }
    } catch (err) {
      setError(err?.message || "Unexpected error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ==================================================
  // AUTH LOADING
  // ==================================================

  if (authLoading) {
    return (
      <div
        className="
          flex
          min-h-[100dvh]
          items-center
          justify-center
          bg-slate-100
          px-4
        "
      >
        <div
          className="
            flex
            items-center
            gap-3
            rounded-2xl
            border
            border-slate-200
            bg-white
            px-5
            py-4
            text-sm
            font-medium
            text-slate-600
            shadow-lg
          "
        >
          <span
            className="
              h-5
              w-5
              animate-spin
              rounded-full
              border-2
              border-sky-200
              border-t-sky-600
            "
          />

          <span>Checking authentication...</span>
        </div>
      </div>
    );
  }

  // ==================================================
  // PAGE
  // ==================================================

  return (
    <>
      <style>
        {`
          *,
          *::before,
          *::after {
            box-sizing: border-box;
          }

          .password-page {
            overflow-wrap: anywhere;
          }

          @media (max-width: 639px) {
            input[type="password"],
            input[type="text"] {
              font-size: 16px;
            }
          }
        `}
      </style>

      <div
        className="
          password-page
          relative
          min-h-[100dvh]
          overflow-x-hidden
          bg-slate-100
        "
      >
        {/* ==================================================
            BACKGROUND DECORATIONS
        ================================================== */}

        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="
              absolute
              -left-28
              -top-28
              h-72
              w-72
              rounded-full
              bg-sky-200/30
              blur-3xl
              sm:h-96
              sm:w-96
            "
          />

          <div
            className="
              absolute
              -bottom-32
              -right-24
              h-80
              w-80
              rounded-full
              bg-blue-300/20
              blur-3xl
              sm:h-[28rem]
              sm:w-[28rem]
            "
          />

          <div
            className="
              absolute
              left-1/2
              top-1/2
              h-48
              w-48
              -translate-x-1/2
              -translate-y-1/2
              rounded-full
              bg-cyan-200/10
              blur-3xl
              sm:h-72
              sm:w-72
            "
          />
        </div>

        {/* ==================================================
            MAIN WRAPPER
        ================================================== */}

        <div
          className="
            relative
            z-10
            flex
            min-h-[100dvh]
            items-start
            justify-center
            p-2
            sm:p-4
            md:p-6
            lg:items-center
            lg:p-8
          "
        >
          <div
            className="
              flex
              w-full
              max-w-5xl
              flex-col
              overflow-hidden
              rounded-2xl
              border
              border-white/80
              bg-white/90
              shadow-2xl
              backdrop-blur-xl
              sm:rounded-3xl
              lg:min-h-[650px]
              lg:flex-row
            "
          >
            {/* ==================================================
                LEFT INFORMATION PANEL
            ================================================== */}

            <aside
              className="
                hidden
                w-full
                shrink-0
                flex-col
                justify-between
                bg-[linear-gradient(145deg,rgb(15,31,51)_0%,rgb(26,58,92)_50%,rgb(30,73,118)_100%)]
                p-6
                text-white
                lg:flex
                lg:w-[38%]
                lg:p-7
                xl:p-8
              "
            >
              <div>
                {/* Logo */}
                <div className="mb-8 flex items-center gap-3">
                  <div
                    className="
                      flex
                      h-11
                      w-11
                      shrink-0
                      items-center
                      justify-center
                      overflow-hidden
                      rounded-xl
                      bg-white
                      shadow-lg
                    "
                  >
                    <img
                      src="/logo.jpeg"
                      alt="MRHealth"
                      className="h-full w-full object-cover"
                    />
                  </div>

                  <div className="min-w-0">
                    <p className="text-lg font-bold">MRHealth</p>

                    <p className="text-xs text-sky-200">Healthcare Together</p>
                  </div>
                </div>

                {/* Heading */}
                <div className="mb-8">
                  <div
                    className="
                      mb-4
                      inline-flex
                      items-center
                      gap-2
                      rounded-full
                      border
                      border-sky-300/20
                      bg-sky-400/10
                      px-3
                      py-1.5
                      text-xs
                      font-semibold
                      text-sky-200
                    "
                  >
                    <FaShieldAlt />

                    <span>Account Security</span>
                  </div>

                  <h1
                    className="
                      text-2xl
                      font-bold
                      leading-tight
                      tracking-tight
                      xl:text-3xl
                    "
                  >
                    Protect your
                    <span className="block text-sky-300">account</span>
                  </h1>

                  <p
                    className="
                      mt-4
                      max-w-md
                      text-sm
                      leading-6
                      text-slate-300
                    "
                  >
                    Keep your MRHealth account secure by regularly using a
                    strong and unique password.
                  </p>
                </div>

                {/* Security points */}
                <div className="space-y-3">
                  {[
                    {
                      icon: FaKey,
                      title: "Use a strong password",
                      text: "Choose a password that is difficult to guess.",
                    },
                    {
                      icon: FaShieldAlt,
                      title: "Protect your account",
                      text: "Never share your password with anyone.",
                    },
                    {
                      icon: FaLock,
                      title: "Stay secure",
                      text: "Avoid using the same password everywhere.",
                    },
                  ].map((item) => {
                    const Icon = item.icon;

                    return (
                      <div
                        key={item.title}
                        className="
                          flex
                          items-start
                          gap-3
                          rounded-xl
                          border
                          border-white/10
                          bg-white/5
                          p-3
                        "
                      >
                        <div
                          className="
                            flex
                            h-9
                            w-9
                            shrink-0
                            items-center
                            justify-center
                            rounded-lg
                            bg-sky-400/15
                            text-sky-300
                          "
                        >
                          <Icon className="text-sm" />
                        </div>

                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold">
                            {item.title}
                          </h3>

                          <p
                            className="
                              mt-0.5
                              text-xs
                              leading-5
                              text-slate-400
                            "
                          >
                            {item.text}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Bottom quote */}
              <div
                className="
                  mt-8
                  rounded-xl
                  border
                  border-white/10
                  bg-white/5
                  p-4
                "
              >
                <p className="text-sm font-medium text-sky-200">
                  वैद्यो नारायणो हरिः
                </p>

                <p className="mt-1 text-xs text-slate-400">
                  “The healer is a form of God.”
                </p>
              </div>
            </aside>

            {/* ==================================================
                RIGHT CONTENT
            ================================================== */}

            <main
              className="
                flex
                min-w-0
                flex-1
                flex-col
                bg-white
              "
            >
              {/* ==================================================
                  HEADER
              ================================================== */}

              <header
                className="
                  shrink-0
                  border-b
                  border-slate-200
                  bg-white
                  px-4
                  py-4
                  sm:px-6
                  sm:py-5
                  lg:px-8
                "
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      disabled={loading}
                      className="
                        flex
                        h-10
                        w-10
                        shrink-0
                        items-center
                        justify-center
                        rounded-xl
                        border
                        border-slate-200
                        bg-white
                        text-slate-600
                        shadow-sm
                        transition
                        hover:border-sky-200
                        hover:bg-sky-50
                        hover:text-sky-600
                        active:scale-95
                        disabled:cursor-not-allowed
                        disabled:opacity-50
                      "
                      aria-label="Go back"
                    >
                      <FaArrowLeft className="text-sm" />
                    </button>

                    <div className="min-w-0">
                      <h2
                        className="
                          truncate
                          text-lg
                          font-bold
                          tracking-tight
                          text-slate-800
                          sm:text-xl
                        "
                      >
                        Change Password
                      </h2>

                      <p className="truncate text-xs text-slate-500 sm:text-sm">
                        Update your account password
                      </p>
                    </div>
                  </div>

                  <div
                    className="
                      hidden
                      shrink-0
                      items-center
                      gap-2
                      rounded-full
                      border
                      border-sky-100
                      bg-sky-50
                      px-3
                      py-1.5
                      text-xs
                      font-semibold
                      text-sky-700
                      sm:flex
                    "
                  >
                    <FaShieldAlt />

                    <span>Secure</span>
                  </div>
                </div>
              </header>

              {/* ==================================================
                  CONTENT
              ================================================== */}

              <div className="flex-1">
                <div
                  className="
                    mx-auto
                    w-full
                    max-w-2xl
                    px-3
                    py-5
                    sm:px-6
                    sm:py-7
                    md:px-8
                    lg:py-8
                  "
                >
                  {/* ==================================================
                      MOBILE SECURITY CARD
                  ================================================== */}

                  <div
                    className="
                      mb-5
                      rounded-2xl
                      border
                      border-sky-100
                      bg-[linear-gradient(135deg,#f0f9ff,#ffffff)]
                      p-4
                      shadow-sm
                      lg:hidden
                      sm:p-5
                    "
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="
                          flex
                          h-11
                          w-11
                          shrink-0
                          items-center
                          justify-center
                          rounded-xl
                          bg-sky-100
                          text-sky-600
                        "
                      >
                        <FaShieldAlt />
                      </div>

                      <div className="min-w-0">
                        <h3 className="text-sm font-bold text-slate-800 sm:text-base">
                          Keep your account secure
                        </h3>

                        <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm">
                          Use a strong password and never share it with anyone.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* ==================================================
                      FORM CARD
                  ================================================== */}

                  <section
                    className="
                      overflow-hidden
                      rounded-2xl
                      border
                      border-slate-200
                      bg-white
                      shadow-sm
                    "
                  >
                    {/* Section header */}
                    <div
                      className="
                        border-b
                        border-slate-100
                        bg-slate-50/80
                        px-4
                        py-3.5
                        sm:px-5
                        sm:py-4
                      "
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="
                            flex
                            h-9
                            w-9
                            shrink-0
                            items-center
                            justify-center
                            rounded-xl
                            bg-sky-100
                            text-sky-600
                          "
                        >
                          <FaLock className="text-sm" />
                        </div>

                        <div className="min-w-0">
                          <h3 className="text-sm font-bold text-slate-800 sm:text-base">
                            Password Details
                          </h3>

                          <p className="text-[11px] text-slate-500 sm:text-xs">
                            Enter your current and new password
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-4 sm:p-5 md:p-6">
                      <div className="space-y-5">
                        {/* ==================================================
                            CURRENT PASSWORD
                        ================================================== */}

                        <PasswordField
                          id="oldPassword"
                          label="Current Password"
                          value={oldPassword}
                          onChange={setOldPassword}
                          showPassword={showOldPassword}
                          setShowPassword={setShowOldPassword}
                          placeholder="Enter your current password"
                          setError={setError}
                          disabled={loading}
                        />

                        {/* ==================================================
                            NEW PASSWORD
                        ================================================== */}

                        <PasswordField
                          id="newPassword"
                          label="New Password"
                          value={newPassword}
                          onChange={setNewPassword}
                          showPassword={showNewPassword}
                          setShowPassword={setShowNewPassword}
                          placeholder="Enter your new password"
                          setError={setError}
                          disabled={loading}
                        />

                        {/* Password rules */}
                        <div
                          className="
                            rounded-xl
                            border
                            border-slate-100
                            bg-slate-50
                            p-3.5
                          "
                        >
                          <p className="mb-2 text-xs font-semibold text-slate-700">
                            Password requirements
                          </p>

                          <div
                            className="
                              grid
                              grid-cols-1
                              gap-2
                              sm:grid-cols-2
                            "
                          >
                            {[
                              "At least 6 characters",
                              "Use a unique password",
                              "Avoid easily guessed information",
                              "Do not reuse old password",
                            ].map((rule) => (
                              <div
                                key={rule}
                                className="
                                  flex
                                  items-start
                                  gap-2
                                  text-[11px]
                                  leading-5
                                  text-slate-500
                                  sm:text-xs
                                "
                              >
                                <FaCheckCircle
                                  className="
                                    mt-0.5
                                    shrink-0
                                    text-sky-500
                                  "
                                />

                                <span>{rule}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* ==================================================
                            CONFIRM PASSWORD
                        ================================================== */}

                        <PasswordField
                          id="confirmPassword"
                          label="Confirm New Password"
                          value={confirmPassword}
                          onChange={setConfirmPassword}
                          showPassword={showConfirmPassword}
                          setShowPassword={setShowConfirmPassword}
                          placeholder="Re-enter your new password"
                          setError={setError}
                          disabled={loading}
                        />

                        {/* ==================================================
                            ERROR
                        ================================================== */}

                        {error && (
                          <div
                            className="
                              flex
                              items-start
                              gap-3
                              rounded-xl
                              border
                              border-red-200
                              bg-red-50
                              p-3.5
                              text-sm
                              text-red-700
                            "
                          >
                            <FaExclamationCircle className="mt-0.5 shrink-0" />

                            <p className="min-w-0 break-words leading-5">
                              {error}
                            </p>
                          </div>
                        )}

                        {/* ==================================================
                            SUCCESS
                        ================================================== */}

                        {successMessage && (
                          <div
                            className="
                              flex
                              items-start
                              gap-3
                              rounded-xl
                              border
                              border-emerald-200
                              bg-emerald-50
                              p-3.5
                              text-sm
                              text-emerald-700
                            "
                          >
                            <FaCheckCircle className="mt-0.5 shrink-0" />

                            <p className="min-w-0 break-words leading-5">
                              {successMessage}
                            </p>
                          </div>
                        )}

                        {/* ==================================================
                            BUTTONS
                        ================================================== */}

                        <div
                          className="
                            flex
                            flex-col-reverse
                            gap-3
                            pt-1
                            sm:flex-row
                            sm:justify-end
                          "
                        >
                          <button
                            type="button"
                            onClick={() => navigate(-1)}
                            disabled={loading}
                            className="
                              flex
                              min-h-11
                              w-full
                              items-center
                              justify-center
                              gap-2
                              rounded-xl
                              border
                              border-slate-200
                              bg-white
                              px-5
                              py-2.5
                              text-sm
                              font-semibold
                              text-slate-700
                              shadow-sm
                              transition
                              hover:border-sky-200
                              hover:bg-sky-50
                              hover:text-sky-700
                              disabled:cursor-not-allowed
                              disabled:opacity-50
                              sm:w-auto
                            "
                          >
                            <FaArrowLeft className="text-xs" />

                            <span>Cancel</span>
                          </button>

                          <button
                            type="submit"
                            disabled={loading}
                            className="
                              flex
                              min-h-11
                              w-full
                              items-center
                              justify-center
                              gap-2
                              rounded-xl
                              bg-[linear-gradient(135deg,rgb(14,165,233),rgb(30,73,118))]
                              px-6
                              py-2.5
                              text-sm
                              font-bold
                              text-white
                              shadow-lg
                              shadow-sky-900/10
                              transition
                              hover:-translate-y-0.5
                              hover:shadow-xl
                              active:translate-y-0
                              disabled:cursor-not-allowed
                              disabled:opacity-60
                              disabled:hover:translate-y-0
                              sm:w-auto
                            "
                          >
                            {loading ? (
                              <>
                                <span
                                  className="
                                    h-4
                                    w-4
                                    animate-spin
                                    rounded-full
                                    border-2
                                    border-white/30
                                    border-t-white
                                  "
                                />

                                <span>Changing...</span>
                              </>
                            ) : (
                              <>
                                <FaLock className="text-xs" />

                                <span>Change Password</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </form>
                  </section>

                  {/* ==================================================
                      BOTTOM SECURITY NOTE
                  ================================================== */}

                  <div
                    className="
                      mt-4
                      flex
                      items-start
                      gap-3
                      rounded-xl
                      border
                      border-slate-200
                      bg-white/70
                      p-3.5
                      text-xs
                      text-slate-500
                      shadow-sm
                    "
                  >
                    <FaShieldAlt
                      className="
                        mt-0.5
                        shrink-0
                        text-sky-500
                      "
                    />

                    <p className="leading-5">
                      After successfully changing your password, you will be
                      logged out and redirected to the login page for security.
                    </p>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChangePasswordPage;
